import { Typography } from '@mui/material';
import { debounce } from 'lodash';
import * as _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';

import VNCheckBox from '@/components/VNCheckBox/VNCHeckBox';
import VNDatePicker from '@/components/VNDatePicker/VNDatePicker';
import { type DropdownOption, VNDropdown } from '@/components/VNDropdown';
import VNSelect from '@/components/VNSelect/VNSelect';
import VNTags from '@/components/VNTags/VNTags';
import VNTextField from '@/components/VNTextField/VNTextField';
import { AssetSource } from '@/enums';
import type { AccessSchedule } from '@/models/channel';
import { CONTENT_SHARING_BEHAVIOR_OPTIONS } from '@/models/Ou';
import { applicationDataSelector } from '@/store/slices/application-data';
import { fuelUserAccessSliceSelector } from '@/store/slices/fuelUserAccess';
import { accessUtils } from '@/utils/accessUtils';
import useAppUtils from '@/utils/appUtils';
import clientUtils from '@/utils/clientUtils';
import itemUtils from '@/utils/itemUtils';

import Metadata from './Metadata';

const CONTENT_SHARING_SELECT_OPTIONS = CONTENT_SHARING_BEHAVIOR_OPTIONS.map((o) => ({ label: o.label, value: o.value }));

interface Props {
  isMultiple?: boolean;
  hasMov?: boolean;
  isNew?: boolean;
  isMovie?: boolean;
}

function VideoForm({ isMultiple, hasMov = false, isNew = false, isMovie = false }: Props) {
  const [allowMultiCategorySelect, setAllowMultiCategorySelect] = useState(true);
  const { ouData } = useSelector(applicationDataSelector);
  const { permissions } = useSelector(fuelUserAccessSliceSelector);
  const { getValues, setValue, watch, control } = useFormContext();
  const { alert, vnConfirm } = useAppUtils();
  const [embargoScheduleError, setEmbargoScheduleError] = useState<string | null>(null);
  const [expireScheduleError, setExpireScheduleError] = useState<string | null>(null);
  const [showContentSharing, setShowContentSharing] = useState(false);

  const [previousTags, setPreviousTags] = useState(getValues('tags') || []);
  // const [previousCommon, setPreviousCommon] = useState(itemUtils.getFullTagsFromCommonMetadata(getValues('common')) || []);
  const itemCategories = useWatch({ name: 'categories', control });
  const tags = watch('tags');
  const common = watch('common');
  const commonCategories = watch('common.categories');
  const schemaType = watch('schemaType');
  const sourceType = watch('source');
  const contentSharingBehavior = watch('contentSharingBehavior');
  const originalAssetData = watch('originalAssetData');
  const isClipGroup = sourceType === 'ClipGroup';
  const canEditContentSharing = accessUtils.canEditContentSharing(permissions, originalAssetData);
  const lockAccessControls = accessUtils.lockAccessControls(permissions, ouData, contentSharingBehavior, schemaType, sourceType);

  const handleContentSharingBeforeChange = async (newValue: string, oldValue: string) => {
    // console.log('handleContentSharingBeforeChange', { newValue, oldValue });
    const accessSettings = getValues('accessSettings');
    if (!accessUtils.shouldConfirmShareWithParentPortalScheduleClear(permissions, oldValue, newValue, accessSettings)) {
      return true;
    }

    const confirmed = await vnConfirm(accessUtils.SHARE_WITH_PARENT_PORTAL_SCHEDULE_CLEAR_CONFIRM);
    if (!confirmed) {
      return false;
    }

    accessUtils.clearAccessSchedulesInForm(setValue);
    setValue('contentSharingBehavior', newValue);
    return true;
  };

  useEffect(() => {
    // console.log('schemaType changed', { schemaType, ouData, isNew, sourceType, contentSharingBehavior });

    if (isNew) {
      // For new assets, set the content sharing behavior to the OU default, or null them out if not a video
      if (schemaType === 'Video' && ouData?.metaData?.enableCrossOuSharing === true) {
        setShowContentSharing(true);
        const currentContentSharingBehavior = getValues('contentSharingBehavior');
        // console.log('schemaType changed', { currentContentSharingBehavior });
        if (currentContentSharingBehavior == null) {
          const newContentSharingBehavior =
            ouData?.metaData?.enableCrossOuSharing === true ? ouData?.metaData?.contentSharing?.behavior ?? 'DoNotShare' : null;
          setValue('contentSharingBehavior', newContentSharingBehavior);
        }
      } else {
        setShowContentSharing(false);
        setValue('contentSharingBehavior', null);
      }
    } else {
      // for existing assets, show content sharing if the schema type is video and the source type is eligible for content sharing
      // AssetSource.Ingest,
      // AssetSource.SubClip,
      // AssetSource.LiveStreamManager,
      // AssetSource.LiveEncoder,
      // AssetSource.AdVariant,
      // AssetSource.ClipGroup,
      // Basically we exclude NestedClip, ExternalClip, and ClipGroup

      const currentContentSharingBehavior = originalAssetData?.data?.contentSharing?.behavior;
      // eslint-disable-next-line no-lonely-if
      if (
        currentContentSharingBehavior != null ||
        (ouData?.metaData?.enableCrossOuSharing === true &&
          schemaType === 'Video' &&
          accessUtils.isContentSharingEligibleSource(sourceType))
      ) {
        setShowContentSharing(true);
      } else {
        setShowContentSharing(false);
      }
    }
  }, [schemaType]);

  useEffect(() => {
    // console.log('video.tsx loading', { ouData, isNew, schemaType, sourceType });

    // Set allowMultipleCategories
    const allowMultipleCategories = ouData?.metaData?.allowMultipleCategories !== false;
    if (allowMultipleCategories === true || itemCategories?.length > 1) {
      setAllowMultiCategorySelect(true);
    } else {
      setAllowMultiCategorySelect(false);
    }
  }, []);

  const categoryOptions = useMemo<DropdownOption[]>(() => {
    const myFilteredCategories = itemUtils.getFilteredCategories(ouData?.categories, itemCategories);

    return (myFilteredCategories ?? []).map((c) => ({
      id: c.id,
      label: c.name,
    }));
  }, [ouData?.categories, itemCategories]);
  // When the categories is a single select then it just writes a string as the value, and
  // this code will transform it back to an array so its always an array
  useEffect(() => {
    // console.log('commonCategories changed', { commonCategories });
    if (_.isString(commonCategories)) {
      setValue('common.categories', [commonCategories]);
    }
  }, [commonCategories]);

  const onEmbargoDateChanged = (oldEmbargo: string, newEmbargo: string) => {
    const accessSchedules: AccessSchedule[] = _.cloneDeep(getValues('accessSettings.temporal.accessSchedules')) ?? [];
    const { accessSchedules: updatedSchedules, error } = accessUtils.applyEmbargoSet(accessSchedules, oldEmbargo, newEmbargo);

    if (error) {
      setValue('embargoDate', oldEmbargo);
      setEmbargoScheduleError(error);
      alert(error, 'warning');
      return;
    }

    setEmbargoScheduleError(null);
    setValue('accessSettings.temporal.accessSchedules', updatedSchedules);
  };

  const onExpireDateChanged = (oldExpire: string, newExpire: string) => {
    const accessSchedules: AccessSchedule[] = _.cloneDeep(getValues('accessSettings.temporal.accessSchedules')) ?? [];
    const { accessSchedules: updatedSchedules, error } = accessUtils.applyExpireSet(accessSchedules, oldExpire, newExpire);

    if (error) {
      setValue('expireDate', oldExpire);
      setExpireScheduleError(error);
      alert(error, 'warning');
      return;
    }

    setExpireScheduleError(null);
    setValue('accessSettings.temporal.accessSchedules', updatedSchedules);
  };

  function compareTags(current: string[], previous: string[]) {
    let direction = 0;
    let diff = [] as string[];
    if (current.length < previous.length) {
      direction = -1;
      diff = previous.filter((tag) => !current.includes(tag));
    } else if (current.length > previous.length) {
      direction = 1;
      diff = current.filter((tag) => !previous.includes(tag));
    }

    return { diff, direction };
  }

  useEffect(() => {
    const debouncedUpdate = debounce(() => {
      const tagChange = compareTags(tags, previousTags);

      if (tagChange.direction > 0 || tagChange.direction < 0) {
        const commonFromTags = itemUtils.extractCommonTags(tags ?? []);
        if (commonFromTags.allTags.join('|') !== (tags ?? []).join('|')) {
          setValue('tags', commonFromTags.allTags);
        }
        setPreviousTags(commonFromTags.allTags);
        // #region Apply extracted tags onto existing common (preserve highlightMessage, categories, etc.)
        const mergedCommon = {
          ...(common ?? {}),
          cast: commonFromTags.cast,
          producers: commonFromTags.producer,
          directors: commonFromTags.director,
          genres: commonFromTags.genre,
          mpaaRating: itemUtils.getFirstOrDefault(commonFromTags.ratingMpaa, ''),
          bbfcRating: itemUtils.getFirstOrDefault(commonFromTags.ratingBbfc, ''),
          releaseYear: commonFromTags.releaseYear ?? '',
          tags: commonFromTags.tags,
          series: commonFromTags.series,
          season: commonFromTags.season,
          episode: commonFromTags.episode,
          episodeOnscreen: commonFromTags.episodeOnscreen,
          episodeXmltv: commonFromTags.episodeXmltv,
          europeanFSKRating: commonFromTags.europeanFskRating != null ? String(commonFromTags.europeanFskRating) : '',
          usParentalRatings: commonFromTags.usParentalRatings,
          leagueName: commonFromTags.leagueName,
          teamName: commonFromTags.teamName,
          originalAirDate: commonFromTags.originalAirDate,
          networkValue: commonFromTags.networkValue,
          tmsidEpisode: commonFromTags.tmsidEpisode,
          tmsidSeries: commonFromTags.tmsidSeries,
          programType: commonFromTags.programType,
          categories: common?.categories,
        };
        // #endregion
        setValue('common', mergedCommon);
      }
    }, 300);

    debouncedUpdate();

    return () => {
      debouncedUpdate.cancel();
    };
  }, [tags]);

  const debouncedUpdateRef = useRef<any>(null);

  useEffect(() => {
    // Create a debounced function
    debouncedUpdateRef.current = debounce((commonData) => {
      const commonTagsArray = itemUtils.getFullTagsFromCommonMetadata(commonData);
      const extractedTagsObj = itemUtils.extractCommonTags(tags);
      const basicTags = extractedTagsObj.tags;
      const extractedTags = [] as string[];
      Object.keys(extractedTagsObj).forEach((key) => {
        if (key !== 'tags' && key !== 'allTags') {
          extractedTags.push(...extractedTagsObj[key]);
        }
      });

      const commonTagsChange = compareTags(commonTagsArray, extractedTags);
      if (commonTagsChange.direction > 0 || commonTagsChange.direction < 0) {
        const newTags = Array.from(new Set([...basicTags, ...commonTagsArray]));
        setValue('tags', newTags);
        setPreviousTags(newTags);
      }
    }, 300);

    const subscription = watch((value, { name }) => {
      if (name && name.startsWith('common.')) {
        const commonData = value.common;
        if (debouncedUpdateRef.current) {
          debouncedUpdateRef.current(commonData);
        }
      }
    });
    return () => {
      subscription.unsubscribe();
      debouncedUpdateRef.current?.cancel?.();
    };
  }, [common]);

  return (
    <div className="form">
      <div className="form-grid" style={{ marginBottom: '1em' }}>
        <VNTextField name="schemaType" label="Media Type" disabled />
        <VNDropdown
          label="Categories"
          placeholder="Select Categories"
          searchable="autocomplete"
          multiple={allowMultiCategorySelect}
          name="common.categories"
          control={control}
          options={categoryOptions}
          chipColor="category"
        />
        {isMultiple ? (
          <VNTextField name="title" label="Title (populated with filename)" disabled />
        ) : (
          <VNTextField name="title" label="Title" rules={{ required: 'Title is required' }} required />
        )}
        <VNTags name="tags" label="Tags" inputTagWidth={300} allowClear />
        <VNDatePicker
          name="publicationDate"
          label={`Published (${ouData.timezoneAbbreviation})`}
          timezone={ouData.timezone}
          rules={{ required: 'Published Date is required' }}
          required
        />

        <div className="sub-row">
          {sourceType !== AssetSource.ExternalClip && !isClipGroup && (
            <VNCheckBox name="applyAutomatedTransitions" label="Include Automated Transitions" />
          )}
          {!isClipGroup && <VNCheckBox name="doNotDelete" label="Retain/Do Not Delete" />}
        </div>

        {sourceType === AssetSource.ExternalClip ? (
          <>
            <VNTextField
              name="synopsis"
              label="Description"
              rows={4}
              disabled={isMultiple}
              required={isMovie}
              rules={
                isMovie
                  ? {
                      required: 'Description is required',
                      validate: (value) => (typeof value === 'string' && value.trim() ? true : 'Description is required'),
                    }
                  : undefined
              }
            />
            <VNTextField
              name="externalHlsFileUrl"
              label="External HLS URL"
              required
              rules={{
                required: 'External HLS URL is required',
                validate: (value) => clientUtils.isValidM3u8Url(value) || 'Invalid External HLS URL',
              }}
              disabled={!isNew}
            />
          </>
        ) : (
          <div className="video-embargo-description-row">
            <VNTextField
              name="synopsis"
              label="Description"
              rows={4}
              disabled={isMultiple}
              required={isMovie}
              rules={
                isMovie
                  ? {
                      required: 'Description is required',
                      validate: (value) => (typeof value === 'string' && value.trim() ? true : 'Description is required'),
                    }
                  : undefined
              }
            />
            <div className="video-embargo-description-row__side">
              <div className="sub-row">
                <div>
                  <VNDatePicker
                    name="embargoDate"
                    label={`Embargo (${ouData.timezoneAbbreviation})`}
                    timezone={ouData.timezone}
                    onDateChanged={onEmbargoDateChanged}
                    disabled={lockAccessControls}
                  />
                  {embargoScheduleError && (
                    <Typography variant="caption" className="text-danger" display="block">
                      {embargoScheduleError}
                    </Typography>
                  )}
                </div>
                <div>
                  <VNDatePicker
                    name="expireDate"
                    label={`Expires (${ouData.timezoneAbbreviation})`}
                    timezone={ouData.timezone}
                    onDateChanged={onExpireDateChanged}
                    disabled={lockAccessControls}
                  />
                  {expireScheduleError && (
                    <Typography variant="caption" className="text-danger" display="block">
                      {expireScheduleError}
                    </Typography>
                  )}
                </div>
              </div>
              {hasMov && (
                <div className="mov-checkbox">
                  <VNCheckBox name="includeAsTransition" label="Include .mov(s) as Transition(s)" />
                </div>
              )}
              {showContentSharing && (
                <div className="video-embargo-description-row__sharing">
                  <VNSelect
                    name="contentSharingBehavior"
                    label="Sharing Behavior"
                    options={CONTENT_SHARING_SELECT_OPTIONS}
                    onBeforeChange={handleContentSharingBeforeChange}
                    disabled={!canEditContentSharing}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <Metadata isMultiple={isMultiple} isNew={isNew} isMovie={isMovie} lockAccessControls={lockAccessControls} />
    </div>
  );
}

export default VideoForm;
